import type { DocumentExtractionV2Invocation } from '@lifeomic/react-client';

export type DocumentExtractionV2SourceResourceType =
  | 'DocumentReference'
  | 'Communication'
  | 'QuestionnaireResponse';

export const DOCUMENT_EXTRACTION_V2_MARKDOWN_ARTIFACT_VERSION =
  '2026-05-13.initial-markdown';

type ExtendedDocumentExtractionV2Invocation = DocumentExtractionV2Invocation & {
  source_resource_type?: DocumentExtractionV2SourceResourceType | null;
  source_resource_id?: string | null;
  output?:
    | (DocumentExtractionV2Invocation['output'] & {
        source_resource_type?: string | null;
        source_resource_id?: string | null;
      })
    | null;
};

export type DocumentExtractionV2InvokeBody = {
  subject_id: string;
  project_id: string;
  source_resource_type: DocumentExtractionV2SourceResourceType;
  source_resource_id: string;
  source_document_reference_id?: string;
  include_clinic_only?: boolean;
  force_regenerate?: boolean;
  reason?: string;
};

export type { ExtendedDocumentExtractionV2Invocation };

const bytesToHex = (bytes: Uint8Array) =>
  Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');

const formatUuidFromBytes = (bytes: Uint8Array) => {
  const uuidBytes = bytes.slice(0, 16);
  uuidBytes[6] = (uuidBytes[6] & 0x0f) | 0x40;
  uuidBytes[8] = (uuidBytes[8] & 0x3f) | 0x80;
  const hex = bytesToHex(uuidBytes);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
};

const digestStableKey = async (stableKey: string) => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      'Web Crypto API (globalThis.crypto.subtle) is not available',
    );
  }
  const hashBuffer = await subtle.digest(
    'SHA-256',
    new TextEncoder().encode(stableKey),
  );
  return formatUuidFromBytes(new Uint8Array(hashBuffer));
};

export const getTaskSourceResourceType = (
  task: DocumentExtractionV2Invocation,
): DocumentExtractionV2SourceResourceType => {
  const extendedTask = task as ExtendedDocumentExtractionV2Invocation;
  const sourceResourceType =
    extendedTask.source_resource_type ??
    extendedTask.output?.source_resource_type;

  if (sourceResourceType === 'Communication') {
    return 'Communication';
  }
  if (sourceResourceType === 'QuestionnaireResponse') {
    return 'QuestionnaireResponse';
  }
  return 'DocumentReference';
};

const normalizeTaskSourceResourceId = (
  sourceResourceType: DocumentExtractionV2SourceResourceType,
  sourceResourceId: string | null | undefined,
) =>
  sourceResourceType === 'DocumentReference'
    ? sourceResourceId?.replace(/^DocumentReference\//, '') ?? null
    : sourceResourceId ?? null;

export const getTaskSourceResourceId = (
  task: DocumentExtractionV2Invocation,
) => {
  const extendedTask = task as ExtendedDocumentExtractionV2Invocation;
  const sourceResourceType = getTaskSourceResourceType(task);

  return normalizeTaskSourceResourceId(
    sourceResourceType,
    extendedTask.source_resource_id ??
      extendedTask.output?.source_resource_id ??
      task.source_document_reference_id ??
      task.output?.source_document_reference_id,
  );
};

export const isTaskForSource = (
  task: DocumentExtractionV2Invocation,
  sourceResourceType: DocumentExtractionV2SourceResourceType,
  sourceResourceId: string,
) =>
  getTaskSourceResourceType(task) === sourceResourceType &&
  getTaskSourceResourceId(task) === sourceResourceId;

const sortNewestFirst = (
  left: DocumentExtractionV2Invocation,
  right: DocumentExtractionV2Invocation,
) => right.created - left.created;

export const selectSourceTask = (
  tasks: DocumentExtractionV2Invocation[],
  sourceResourceType: DocumentExtractionV2SourceResourceType,
  sourceResourceId: string,
) => {
  const sourceTasks = tasks
    .filter(task => isTaskForSource(task, sourceResourceType, sourceResourceId))
    .sort(sortNewestFirst);

  return sourceTasks[0] ?? null;
};

export const getSourceReference = (
  sourceResourceType: DocumentExtractionV2SourceResourceType,
  sourceResourceId: string,
) => `${sourceResourceType}/${sourceResourceId}`;

export const buildSummaryArtifactResourceId = async ({
  accountId,
  projectId,
  subjectId,
  sourceResourceType,
  sourceResourceId,
  artifactVersion = DOCUMENT_EXTRACTION_V2_MARKDOWN_ARTIFACT_VERSION,
}: {
  accountId: string;
  projectId: string;
  subjectId: string;
  sourceResourceType: DocumentExtractionV2SourceResourceType;
  sourceResourceId: string;
  artifactVersion?: string;
}) =>
  digestStableKey(
    [
      accountId,
      projectId,
      subjectId,
      sourceResourceType,
      sourceResourceId,
      artifactVersion,
      'summary',
    ].join('|'),
  );

export const buildLegacyDocumentSummaryArtifactResourceId = async ({
  accountId,
  projectId,
  subjectId,
  sourceDocumentReferenceId,
  artifactVersion = DOCUMENT_EXTRACTION_V2_MARKDOWN_ARTIFACT_VERSION,
}: {
  accountId: string;
  projectId: string;
  subjectId: string;
  sourceDocumentReferenceId: string;
  artifactVersion?: string;
}) =>
  digestStableKey(
    [
      accountId,
      projectId,
      subjectId,
      sourceDocumentReferenceId,
      artifactVersion,
      'summary',
    ].join('|'),
  );

export const buildSummaryArtifactResourceIds = async ({
  accountId,
  projectId,
  subjectId,
  sourceResourceType,
  sourceResourceId,
  artifactVersion = DOCUMENT_EXTRACTION_V2_MARKDOWN_ARTIFACT_VERSION,
}: {
  accountId: string;
  projectId: string;
  subjectId: string;
  sourceResourceType: DocumentExtractionV2SourceResourceType;
  sourceResourceId: string;
  artifactVersion?: string;
}) => {
  const primaryId = await buildSummaryArtifactResourceId({
    accountId,
    projectId,
    subjectId,
    sourceResourceType,
    sourceResourceId,
    artifactVersion,
  });

  if (sourceResourceType !== 'DocumentReference') {
    return [primaryId];
  }

  const legacyId = await buildLegacyDocumentSummaryArtifactResourceId({
    accountId,
    projectId,
    subjectId,
    sourceDocumentReferenceId: sourceResourceId,
    artifactVersion,
  });

  return primaryId === legacyId ? [primaryId] : [primaryId, legacyId];
};
